<script>
  import { afterUpdate, onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Input, Tooltip } from '@sveltestrap/sveltestrap';

  import { QueryParams } from '@client/utils/query-params';

  import { generateUUID } from '@common/utils/strings';

  export let filter = {};
  export let label = '';
  export let tooltipLabel = '';
  export let onClick = () => {};
  export let param = null;
  export let autoClose = 'outside';
  export let search = false;
  export let maxHeight = 'auto';

  let isOpen = false;
  let filtered = filter;
  let selectedLabels = [];
  let initialized = false;

  const id = generateUUID();

  const onClickHandler = (e, item) => {
    if (item.value === 'all') {
      const allChecked = selected.length === Object.keys(filter).length;

      if (allChecked) {
        selected = [];
        selectedLabels = [];
        Object.keys(filter).forEach((name) => (filter[name].enabled = false));
      } else {
        selectedLabels = [];
        selected = Object.keys(filter).map((key) => {
          selectedLabels.push(filter[key].label);
          filter[key].enabled = true;
          return filter[key].value;
        });
      }
    } else {
      filter[item.value].enabled = !filter[item.value].enabled;

      if (selected.includes(item.value)) {
        selected.splice(selected.indexOf(item.value), 1);
        selectedLabels.splice(selectedLabels.indexOf(labels[item.value]), 1);
      } else {
        selected.push(item.value);
        selectedLabels.push(labels[item.value]);
      }
    }

    selected = [...selected];
    selectedLabels = [...selectedLabels];

    filter = { ...filter };

    onClick(e, item.value, filter, selected);
  };

  const onFocus = (e) => {
    e.stopPropagation();
    e.preventDefault();

    return false;
  };

  const onSearch = (e) => {
    const value = e.target.value;
    const keys = Object.keys(filter);

    filtered = keys.reduce((acc, key) => {
      if (key.includes(value)) {
        acc[key] = filter[key];
      }

      return acc;
    }, {});
  };

  const updateQueryParams = () => {
    if (selected.length) {
      QueryParams.set(param, selected);
    } else {
      QueryParams.delete(param);
    }

    selectedLabels = selected.map((value) => labels[value]);
  };

  $: filtered = filter;
  $: selected = Object.keys(filter)
    .map((key) => filter[key].enabled && filter[key].value)
    .filter(Boolean);

  $: labels = Object.keys(filter).reduce((obj, key) => {
    if (!obj[filter[key].value]) {
      obj[filter[key].value] = filter[key].label;
    }

    return obj;
  }, {});

  const initialize = () => {
    const queryParamValue = QueryParams.get(param);

    if (queryParamValue?.length) {
      const value = queryParamValue.split(',');

      selected = Object.keys(filter).filter((key) => {
        const match = value.includes(filter[key].value);
        filter[key].enabled = match;
        return match;
      });
    } else {
      QueryParams.delete(param);
    }

    updateQueryParams();
  };

  afterUpdate(() => {
    if (initialized) {
      if (param && Object.keys(filter).length) {
        updateQueryParams();
      }
    }

    initialized = true;
  });

  onMount(initialize);
</script>

<Dropdown {isOpen} {autoClose} toggle={() => (isOpen = !isOpen)}>
  <DropdownToggle tag="div">
    <div id="filter-{id}" class="filter-button" class:active={selected.length}>
      {label}
      <span>{selected.length}</span>
    </div>
    {#if selected.length && !isOpen}
      <Tooltip target="filter-{id}" placement="right">
        {tooltipLabel}
        {selectedLabels.map((value) => $format(value)).join(', ')}
      </Tooltip>
    {/if}
  </DropdownToggle>
  <DropdownMenu style="max-height: {maxHeight}; overflow: auto;">
    {#if search}
      <DropdownItem on:click={onFocus} class="search-item-container">
        <div class="search-item">
          <input type="text" on:click|preventDefault on:input={onSearch} placeholder="Search" />
        </div>
      </DropdownItem>
    {/if}
    <DropdownItem on:click={(e) => onClickHandler(e, { value: 'all' })} class="px-3 py-2 d-flex">
      <Input type="checkbox" checked={selected.length === Object.keys(filter).length} tabindex={-1} />
      {#if selected.length === Object.keys(filter).length}
        {$format('label.UNSELECT_ALL')}
      {:else}
        {$format('label.SELECT_ALL')}
      {/if}
    </DropdownItem>
    <DropdownItem divider />
    {#each Object.entries(filtered) as [value, config]}
      <DropdownItem on:click={(e) => onClickHandler(e, config)} class="px-3 py-2">
        <div class="filter-item">
          <Input type="checkbox" checked={selected.includes(config.value)} tabindex={-1} />
          <span class="filter-label">
            <slot name="label" {config}>
              {$format(config.label)}
            </slot>
          </span>
        </div>
      </DropdownItem>
    {/each}
  </DropdownMenu>
</Dropdown>

<style lang="scss" src="./multiselect.scss"></style>
